<script setup>
import { computed, watch } from 'vue'
import { useForm, usePage } from '@inertiajs/vue3'

import ContactController from '@/actions/App/Http/Controllers/ContactController'
import BrandButton from '@/components/ui/BrandButton.vue'
import SectionHeading from '@/components/ui/SectionHeading.vue'
import ServiceSelect from '@/components/ui/ServiceSelect.vue'
import { usePlanSelection } from '@/composables/usePlanSelection'
import { findServiceById, serviceSelectGroups } from '@/data/services'

const page = usePage()
const successMessage = computed(() => page.props.flash?.contact_success ?? null)

const { selection, selectPlan, clearSelection, formatSelection } = usePlanSelection()

const form = useForm({
    firstname: '',
    lastname: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    plan: '',
    website: '', // honeypot
})

/**
 * Pre-fill the chosen plan / add-on service when the visitor clicks
 * "Vybrat plán" or "Mám zájem" elsewhere on the page.
 */
watch(selection, (value) => {
    form.plan = formatSelection(value)
})

const serviceGroups = serviceSelectGroups()

/**
 * Obousměrné propojení výběru služby ve formuláři se sdíleným výběrem na úrovni
 * stránky — volba zde se chová stejně jako klik na "Vybrat plán" v ceníku
 * (a naopak). Backendu se posílá textový label přes form.plan (watch níže).
 */
const selectedServiceId = computed({
    get: () => selection.value?.id ?? '',
    set: (id) => (id ? selectPlan(findServiceById(id)) : clearSelection()),
})

const submit = () => {
    form.post(ContactController.store().url, {
        preserveScroll: true,
        onSuccess: () => {
            form.reset()
            clearSelection()
        },
    })
}

const inputBase =
    'mt-2 block w-full rounded-md border bg-white px-3.5 py-3 text-base text-brand-900 shadow-sm focus:ring-2 focus:ring-brand-900/10 focus:outline-none'
const inputClass = computed(() => `${inputBase} border-zinc-300 focus:border-brand-900`)
const inputErrorClass = `${inputBase} border-red-400 focus:border-red-500`

const labelClass = 'block text-sm font-medium text-zinc-600'
const errorClass = 'mt-1.5 text-sm text-red-600'
</script>

<template>
    <section
        class="bg-white py-20 md:py-28"
        id="contact"
    >
        <div class="mx-auto max-w-7xl px-6 lg:px-10">
            <div class="grid gap-12 lg:grid-cols-12 lg:gap-16">
                <div class="lg:col-span-5">
                    <SectionHeading title="Pojďme do toho společně!" />

                    <p class="mt-10 text-base text-zinc-600">
                        Napište nám:
                        <a
                            class="text-brand-900 font-semibold underline-offset-4 hover:underline"
                            href="mailto:solvecount@gmail.com"
                        >
                            solvecount@gmail.com
                        </a>
                    </p>
                </div>

                <div class="lg:col-span-7">
                    <div
                        class="border-brand-200 bg-brand-50/60 rounded-2xl border p-6 md:p-8"
                        v-if="successMessage"
                        role="status"
                        aria-live="polite"
                    >
                        <p class="text-brand-900 text-lg font-semibold">Děkujeme za zprávu.</p>

                        <p class="mt-2 text-base text-zinc-600">
                            {{ successMessage }}
                        </p>
                    </div>

                    <form
                        v-else
                        @submit.prevent="submit"
                        aria-label="Kontaktní formulář"
                    >
                        <div class="space-y-6">
                            <div>
                                <label
                                    :class="labelClass"
                                    for="contact-service"
                                >
                                    Vyberte službu <span class="text-zinc-400">(nepovinné)</span>
                                </label>

                                <ServiceSelect
                                    id="contact-service"
                                    v-model="selectedServiceId"
                                    :groups="serviceGroups"
                                    placeholder="Vyberte plán nebo službu…"
                                    aria-label="Vyberte službu"
                                />

                                <p class="mt-2 text-sm text-zinc-500">
                                    {{
                                        selection
                                            ? 'Doplňte prosím své údaje a my se vám ozveme s nabídkou na míru.'
                                            : 'Nevíte přesně? Nechte nevybráno a poradíme vám.'
                                    }}
                                </p>
                            </div>

                            <div class="grid gap-6 md:grid-cols-2">
                                <div>
                                    <label
                                        :class="labelClass"
                                        for="contact-firstname"
                                    >
                                        Jméno
                                    </label>

                                    <input
                                        id="contact-firstname"
                                        v-model="form.firstname"
                                        :class="form.errors.firstname ? inputErrorClass : inputClass"
                                        type="text"
                                        name="firstname"
                                        autocomplete="given-name"
                                        required
                                    />

                                    <p
                                        v-if="form.errors.firstname"
                                        :class="errorClass"
                                    >
                                        {{ form.errors.firstname }}
                                    </p>
                                </div>

                                <div>
                                    <label
                                        :class="labelClass"
                                        for="contact-lastname"
                                    >
                                        Příjmení
                                    </label>

                                    <input
                                        id="contact-lastname"
                                        v-model="form.lastname"
                                        :class="form.errors.lastname ? inputErrorClass : inputClass"
                                        type="text"
                                        name="lastname"
                                        autocomplete="family-name"
                                        required
                                    />

                                    <p
                                        v-if="form.errors.lastname"
                                        :class="errorClass"
                                    >
                                        {{ form.errors.lastname }}
                                    </p>
                                </div>
                            </div>

                            <div>
                                <label
                                    :class="labelClass"
                                    for="contact-company"
                                >
                                    Název společnosti <span class="text-zinc-400">(nepovinné)</span>
                                </label>

                                <input
                                    id="contact-company"
                                    v-model="form.company"
                                    :class="form.errors.company ? inputErrorClass : inputClass"
                                    type="text"
                                    name="company"
                                    autocomplete="organization"
                                />

                                <p
                                    v-if="form.errors.company"
                                    :class="errorClass"
                                >
                                    {{ form.errors.company }}
                                </p>
                            </div>

                            <div class="grid gap-6 md:grid-cols-2">
                                <div>
                                    <label
                                        :class="labelClass"
                                        for="contact-email"
                                    >
                                        E-mail
                                    </label>

                                    <input
                                        id="contact-email"
                                        v-model="form.email"
                                        :class="form.errors.email ? inputErrorClass : inputClass"
                                        type="email"
                                        name="email"
                                        autocomplete="email"
                                        required
                                    />

                                    <p
                                        v-if="form.errors.email"
                                        :class="errorClass"
                                    >
                                        {{ form.errors.email }}
                                    </p>
                                </div>

                                <div>
                                    <label
                                        :class="labelClass"
                                        for="contact-phone"
                                    >
                                        Telefon <span class="text-zinc-400">(nepovinné)</span>
                                    </label>

                                    <input
                                        id="contact-phone"
                                        v-model="form.phone"
                                        :class="form.errors.phone ? inputErrorClass : inputClass"
                                        type="tel"
                                        name="phone"
                                        autocomplete="tel"
                                    />

                                    <p
                                        v-if="form.errors.phone"
                                        :class="errorClass"
                                    >
                                        {{ form.errors.phone }}
                                    </p>
                                </div>
                            </div>

                            <div>
                                <label
                                    :class="labelClass"
                                    for="contact-message"
                                >
                                    Zpráva <span class="text-zinc-400">(nepovinné)</span>
                                </label>

                                <textarea
                                    id="contact-message"
                                    v-model="form.message"
                                    :class="form.errors.message ? inputErrorClass : inputClass"
                                    name="message"
                                    rows="5"
                                />

                                <p
                                    v-if="form.errors.message"
                                    :class="errorClass"
                                >
                                    {{ form.errors.message }}
                                </p>
                            </div>

                            <!-- Honeypot — hidden from real users, bots fill it -->
                            <div
                                class="hidden"
                                aria-hidden="true"
                            >
                                <label for="contact-website">Web</label>
                                <input
                                    id="contact-website"
                                    v-model="form.website"
                                    type="text"
                                    name="website"
                                    tabindex="-1"
                                    autocomplete="off"
                                />
                            </div>

                            <div class="pt-2">
                                <BrandButton
                                    class="w-full md:w-auto"
                                    size="lg"
                                    type="submit"
                                    :disabled="form.processing"
                                >
                                    {{ form.processing ? 'Odesílám…' : 'Odeslat poptávku' }}
                                </BrandButton>

                                <p class="mt-4 text-xs text-zinc-500">
                                    Odesláním souhlasíte se zpracováním osobních údajů dle GDPR.
                                </p>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </section>
</template>
